import queryString from 'query-string';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import type { InputOnChangeData } from 'semantic-ui-react';

import { navigationService, tokenAuthenticator, userAuthenticator } from 'src/config/rootService';
import { ValidateTokenData } from 'src/modules/auth/models/auth';
import {
    containsNumber,
    containsSpecialCharacter,
    containsUppercaseLetter,
} from 'src/shared/helpers/characterContainValidators';
import { EapAuthLayout } from 'src/shared/layout/EapAuthLayout';

import { AuthorizationBox } from '../components/AuthorizationBox';
import { PasswordValidationInfo } from '../components/PasswordValidationInfo';

import styled from '@emotion/styled';

import { Form, Message as SemanticMessage } from 'semantic-ui-react';

import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { color, font, shadow } from 'src/shared/styles/variables';

const Message = styled(SemanticMessage)({
    '&&.error': {
        padding: '0 0 0 10px',
        border: 'none',
        boxShadow: 'none',
        backgroundColor: 'transparent',
        ...font.roman(font.size.small),
        '&, & a': {
            lineHeight: 1.5,
            letterSpacing: 0.2,
            color: color.monza,
        },
        '& a': font.heavy(font.size.small),
    },
});

const Input = styled(Form.Input)({
    '&&.field': {
        '& label': {
            margin: 0,
            lineHeight: 2,
            color: `${color.fiord} !important`,
            ...font.heavy(font.size.base),
        },
        '& input': {
            transition: 'background-color 0.2s ease-in-out',
            border: ` 1px solid ${color.linkWaterLight} !important`,
            boxShadow: `${shadow.regularShadow} !important`,
        },
        '&.error input': {
            borderradius: 4,
            border: `solid 1px ${color.monza}`,
        },
        '&:not(.error)': {
            '& .icon': {
                display: 'none',
            },
            '& input:not(:focus):valid': {
                backgroundColor: hexToRgba(color.raffia, 0.26),
                maxHeight: 38,
                paddingTop: 14,
                letterSpacing: 0.2,
                color: color.black,
                ...font.medium(font.size.medium),
            },
        },
    },
});

interface State {
    password: string;
    passwordConf: string;
    touched: Record<string, boolean>;
    tokenValid: boolean;
    requestInProgress: boolean;
    [key: string]: unknown;
}

type Props = WithTranslation & RouteComponentProps<ValidateTokenData>;

class EapPasswordResetFormFinal extends React.Component<Props, State> {
    public state: State = {
        password: '',
        passwordConf: '',
        touched: {},
        tokenValid: false,
        requestInProgress: false,
    };

    private getTokenData = (): ValidateTokenData => {
        const searchParams = queryString.parse(this.props.location.search);

        if (searchParams?.uidb64 && searchParams?.token) {
            return {
                uidb64: searchParams.uidb64 as string,
                token: searchParams.token as string,
            };
        }

        return this.props.match.params;
    };

    public componentDidMount(): void {
        tokenAuthenticator
            .verifyPasswordResetToken(this.getTokenData())
            .then(() => this.setState({ tokenValid: true }));
    }

    private handleSubmit = async (event): Promise<void> => {
        event.preventDefault();

        if (!this.isFormValid()) {
            return this.setState({
                touched: {
                    password: true,
                    passwordConf: true,
                },
            });
        }

        this.setState({ requestInProgress: true });

        await userAuthenticator.setNewPassword({
            password: this.state.password,
            ...this.getTokenData(),
        });

        const searchParams = queryString.parse(this.props.location.search);
        if (searchParams && searchParams.role === 'COACH') {
            navigationService.navigate('/provider-dashboard');
        } else {
            navigationService.navigate('/login');
        }
    };

    private isFormValid = (): boolean => this.isPasswordValid() && this.arePasswordsTheSame();

    private isTouchedAndMissing = (name: string): boolean =>
        !(this.state[name] as string).trim() && !!this.state.touched[name];

    private isPasswordValid = (password: string = this.state.password): boolean =>
        containsSpecialCharacter(password) &&
        containsUppercaseLetter(password) &&
        containsNumber(password) &&
        password.trim().length >= 12;

    private arePasswordsTheSame = (): boolean =>
        this.state.password.trim() === this.state.passwordConf.trim();

    private handleBlur = (event: React.FormEvent<HTMLInputElement>): void => {
        const target = event.target as HTMLInputElement;

        this.setState(state => ({
            touched: { ...state.touched, [target.name]: true },
        }));
    };

    private handleChange = (
        _event: React.SyntheticEvent<HTMLInputElement>,
        target: InputOnChangeData,
    ): void => {
        this.setState({ [target.name]: target.value.trim() });
    };

    public render(): JSX.Element | null {
        const { password, touched, tokenValid, requestInProgress } = this.state;
        const { t } = this.props;

        if (!tokenValid) {
            return null;
        }

        const missingPassword = this.isTouchedAndMissing('password');
        const invalidPassword = !missingPassword && !this.isPasswordValid() && touched.password;

        const passwordError = missingPassword || invalidPassword;
        const passwordDoNotMatch = !this.arePasswordsTheSame() && touched.passwordConf;

        return (
            <EapAuthLayout>
                <AuthorizationBox
                    title={t('auth:password_reset_form.password_reset')}
                    buttonText={t('auth:password_reset_form.send')}
                    isFormValid={this.isFormValid()}
                    onSubmit={this.handleSubmit}
                    isFetching={requestInProgress}
                >
                    <>
                        <Input
                            fluid
                            required
                            pattern=".{6,}"
                            label={t('auth:password_reset_form.labels.new_password')}
                            name="password"
                            type="password"
                            autoComplete="new-password"
                            value={password}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                            error={passwordError}
                            icon={passwordError ? 'warning sign' : ''}
                        />

                        {(this.state.touched.password || password) && !this.isPasswordValid() && (
                            <PasswordValidationInfo
                                containsSpecialCharacter={containsSpecialCharacter(password)}
                                containsUppercaseLetter={containsUppercaseLetter(password)}
                                containsNumber={containsNumber(password)}
                                isLongEnough={password.trim().length >= 12}
                            />
                        )}

                        <Input
                            fluid
                            required
                            pattern=".{6,}"
                            label={t('auth:password_reset_form.labels.confirm_new_password')}
                            name="passwordConf"
                            type="password"
                            autoComplete="new-password"
                            value={this.state.passwordConf}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                            error={passwordDoNotMatch}
                            icon={passwordDoNotMatch ? 'warning sign' : ''}
                        />
                        {passwordDoNotMatch && (
                            <Message error>
                                {t('auth:password_reset_form.errors.passwords_do_not_match')}
                            </Message>
                        )}
                    </>
                </AuthorizationBox>
            </EapAuthLayout>
        );
    }
}

export default withTranslation()(EapPasswordResetFormFinal);
